import { Box, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { memo, useEffect, useMemo, useState } from "react";
import { SegmentStatusListItem } from "src/store/api/generatedApi";
import { TimelineSlot } from "./timelineLayout";

interface SegmentCoverageMarkersProps {
  items: readonly SegmentStatusListItem[];
  dayStart: Dayjs;
  segmentLengthSec: number;
  slot: TimelineSlot;
  emphasizeSparseSegments?: boolean;
}

const SECONDS_IN_DAY = 24 * 60 * 60;

const useNowTick = (tickIntervalMs: number): Dayjs => {
  const [now, setNow] = useState<Dayjs>(() => dayjs.utc());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(dayjs.utc());
    }, tickIntervalMs);
    return () => {
      clearInterval(id);
    };
  }, [tickIntervalMs]);
  return now;
};

export const SegmentCoverageMarkers = memo(
  ({
    items,
    dayStart,
    segmentLengthSec,
    slot,
    emphasizeSparseSegments = false,
  }: SegmentCoverageMarkersProps) => {
    const { t } = useTenantTranslation();
    const now = useNowTick(segmentLengthSec * 1000);
    const widthPercent = (segmentLengthSec / SECONDS_IN_DAY) * 100;

    const renderedBars = useMemo(
      () =>
        items.flatMap((item) => {
          const epochTime = dayjs.unix(item.epoch_sec).utc();
          if (!epochTime.isBefore(now)) return [];
          const total = item.total_camera_count ?? 0;
          if (total === 0) return [];
          const ok = item.ok_camera_count ?? 0;
          const leftPercent =
            (epochTime.diff(dayStart, "second") / SECONDS_IN_DAY) * 100;
          const color =
            ok === total
              ? "success.main"
              : ok === 0
                ? "error.main"
                : "warning.main";
          const tooltip = t("coverageTooltip", {
            time: epochTime.format("HH:mm:ss"),
            ok,
            total,
          });
          return [
            <Tooltip key={item.epoch_sec} title={tooltip} arrow>
              <Box
                sx={{
                  position: "absolute",
                  left: `${leftPercent}%`,
                  width: `${widthPercent}%`,
                  // A single segment is a tiny fraction of a day (~0.017% for a
                  // 15s slot), which rounds to sub-pixel and vanishes for sparse
                  // feeds like radio. There, floor the width so isolated activity
                  // shows as a visible tick and paint "covered" marks above gaps
                  // so a lone active slot isn't hidden under adjacent red. Dense
                  // feeds (video) keep the exact percentage width and default
                  // stacking so their coverage bar renders unchanged.
                  ...(emphasizeSparseSegments
                    ? {
                        minWidth: "2px",
                        zIndex: color === "success.main" ? 1 : 0,
                      }
                    : {}),
                  height: "100%",
                  bgcolor: color,
                  opacity: 0.75,
                }}
              />
            </Tooltip>,
          ];
        }),
      [items, dayStart, widthPercent, now, t, emphasizeSparseSegments]
    );

    return (
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: slot.top,
          height: slot.height,
        }}
      >
        {renderedBars}
      </Box>
    );
  }
);
SegmentCoverageMarkers.displayName = "SegmentCoverageMarkers";
